import { Loader2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useNotification } from '../../contexts/NotificationContext'
import { getPrizeWinners } from '../../lib/prizeWinners'
import { assignPrizeWinners, searchUsersForPrize } from '../../services/prizeService'
import PrizeWinnersPicker from '../PrizeWinnersPicker'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'

// Admin dialog that sets who won a prize. More than one winner = a tie
// (ticket bdec6a1f), so the admin no longer clones the prize to award it twice.
export default function AssignWinnersModal({ prize, onClose, onSave }) {
  const [winners, setWinners] = useState(() =>
    getPrizeWinners(prize).map((w) => ({ id: w.id, full_name: w.full_name }))
  )
  const [saving, setSaving] = useState(false)
  const notify = useNotification()

  const searchUsers = useCallback(async (query) => {
    try {
      const data = await searchUsersForPrize({
        p_query: query,
        p_group_id: prize.group_id || null,
        p_limit: 10,
      })
      if (!data.success) throw new Error(data.error)
      return data.users || []
    } catch (error) {
      console.error('Error searching users:', error)
      return []
    }
  }, [prize.group_id])

  const handleAssign = async () => {
    setSaving(true)
    try {
      const data = await assignPrizeWinners({
        p_prize_id: prize.id,
        p_winner_user_ids: winners.map((w) => w.id),
      })
      if (!data.success) throw new Error(data.error)

      notify.success(
        winners.length === 0 ? 'Winners removed'
          : winners.length === 1 ? 'Winner assigned!'
            : `${winners.length} tied winners assigned!`
      )
      onSave()
      onClose()
    } catch (err) {
      console.error('Error assigning winners:', err)
      notify.error(err.message || 'Failed to assign winners')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Winners for "{prize.name}"</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Winners</label>
            <PrizeWinnersPicker
              winners={winners}
              onChange={setWinners}
              onSearch={searchUsers}
              disabled={saving}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {prize.group_id ? 'Searching within group members' : 'Searching all users (global prize)'}.
              {' '}Add more than one person for a tie.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={onClose} disabled={saving} className="sm:order-1">
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={saving} className="sm:order-2">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : 'Save Winners'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
